import { useEffect, useRef } from "react";
import { Paperclip, Send, Square } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { AIModelPill } from "./AIModelPill";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

export function AIComposer({
	value,
	onChange,
	onSubmit,
	isLoading = false,
	onStop,
	placeholder,
	model,
	availableModels = [],
	onModelChange,
	contextChips = [],
	showAttachments = false,
	disabled = false,
	testId,
}) {
	const { t } = useLanguage();
	const textareaRef = useRef(null);

	useEffect(() => {
		const textarea = textareaRef.current;
		if (!textarea) return;
		textarea.style.height = "auto";
		const nextHeight = Math.min(textarea.scrollHeight, 192);
		textarea.style.height = `${Math.max(nextHeight, 60)}px`;
	}, [value]);

	const handleKeyDown = (event) => {
		if (event.key !== "Enter" || event.shiftKey) return;
		event.preventDefault();
		if (!isLoading && !disabled) onSubmit?.();
	};

	return (
		<div className="space-y-2 rounded-sm border border-border bg-secondary/60 p-2">
			{contextChips.length > 0 && (
				<div className="flex flex-wrap gap-1.5 px-1 pt-1">
					{contextChips.map((chip) => (
						<button
							type="button"
							key={chip.id || chip.label}
							onClick={chip.onClick}
							disabled={!chip.onClick || disabled}
							className="rounded-sm border border-border bg-card px-2 py-1 text-[10px] text-muted-foreground hover:border-volt/50 hover:text-foreground disabled:cursor-default disabled:opacity-100"
						>
							{chip.label}
						</button>
					))}
				</div>
			)}

			<Textarea
				textareaRef={textareaRef}
				value={value}
				onChange={(event) => onChange?.(event.target.value)}
				onKeyDown={handleKeyDown}
				placeholder={placeholder}
				disabled={disabled || isLoading}
				rows={1}
				data-testid={testId}
				className="min-h-[60px] max-h-48 resize-none overflow-y-auto border-transparent bg-transparent px-2 py-1.5 text-sm shadow-none focus-visible:ring-0"
			/>

			<div className="flex items-center justify-between gap-2 border-t border-border/60 px-1 pt-2">
				<div className="flex min-w-0 items-center gap-2">
					<AIModelPill
						model={model}
						availableModels={availableModels}
						onChange={onModelChange}
						compact
					/>
					{showAttachments && (
						<Button
							variant="ghost"
							size="icon"
							className="h-8 w-8 rounded-sm"
							aria-label={t("aiAttach")}
							type="button"
						>
							<Paperclip className="h-4 w-4" />
						</Button>
					)}
				</div>
				{isLoading && onStop ? (
					<Button
						variant="destructive"
						onClick={onStop}
						className="h-9 rounded-sm bg-blaze/10 text-blaze hover:bg-blaze/20"
						aria-label={t("aiStop")}
						type="button"
					>
						<Square className="h-3.5 w-3.5 fill-current" />
						<span className="sr-only">{t("aiStop")}</span>
					</Button>
				) : (
					<Button
						onClick={onSubmit}
						disabled={!value.trim() || isLoading || disabled}
						className="h-9 rounded-sm bg-volt hover:bg-volt/90"
						aria-label={t("aiSend")}
						type="button"
					>
						<Send className="h-4 w-4" />
						<span className="sr-only">{t("aiSend")}</span>
					</Button>
				)}
			</div>
		</div>
	);
}
